import { Box, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';

import { SkeletonChart } from 'reports/components';

const DEFAULT_BORDER_WIDTH = 2;
const DEFAULT_POINT_RADIUS = 5;
const DEFAULT_POINT_HOVER_RADIUS = 7;
const DEFAULT_POINT_BORDER_WIDTH = 2;
const DEFAULT_TENSION = 0.4;

export interface ILineChartDataset {
  borderColor: string;
  borderWidth?: number;
  data: number[];
  fill?: boolean;
  label: string;
  pointBackgroundColor?: string;
  pointBorderColor?: string;
  pointBorderWidth?: number;
  pointHoverRadius?: number;
  pointRadius?: number;
  tension?: number;
}

export interface ILineChartData {
  datasets: ILineChartDataset[];
  labels: string[];
}

interface IProps {
  data: ILineChartData;
  height?: number | string;
  isLoading?: boolean;
  options?: ChartOptions<'line'>;
}

export const LineChart = ({ data, height, isLoading, options }: IProps) => {
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.neutral[200];
  const pointBorderColor = theme.palette.background.surface;

  const defaultOptions: ChartOptions<'line'> = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: textColor,
            pointStyle: 'circle',
            usePointStyle: true,
          },
          position: 'bottom',
        },
        tooltip: {
          enabled: true,
        },
      },
      responsive: true,
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: textColor,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: gridColor,
          },
          ticks: {
            color: textColor,
          },
        },
      },
    }),
    [gridColor, textColor]
  );

  const enrichedData = useMemo(
    () => ({
      ...data,
      datasets: data.datasets.map((dataset) => ({
        ...dataset,
        borderWidth: dataset.borderWidth ?? DEFAULT_BORDER_WIDTH,
        fill: dataset.fill ?? false,
        pointBackgroundColor: dataset.pointBackgroundColor ?? pointBorderColor,
        pointBorderColor: dataset.pointBorderColor ?? dataset.borderColor,
        pointBorderWidth: dataset.pointBorderWidth ?? DEFAULT_POINT_BORDER_WIDTH,
        pointHoverRadius: dataset.pointHoverRadius ?? DEFAULT_POINT_HOVER_RADIUS,
        pointRadius: dataset.pointRadius ?? DEFAULT_POINT_RADIUS,
        tension: dataset.tension ?? DEFAULT_TENSION,
      })),
    }),
    [data, pointBorderColor]
  );

  const mergedOptions = options ? merge({}, defaultOptions, options) : defaultOptions;

  if (isLoading) {
    return <SkeletonChart height={height} />;
  }

  return (
    <Box sx={{ height }}>
      <Line data={enrichedData} options={mergedOptions} />
    </Box>
  );
};
